import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Sankey from './index';
import SankeyControls from './SankeyControls';
import ExportChartButtons from '../export-chart-buttons';
import { useSankey } from '../../hooks/useSankey';

const SankeyContainer = ({ 
  type, 
  id, 
  title, 
  showControls = true,
  initialParams = {},
  controlsConfig = {}
}) => {
  const [params, setParams] = useState(initialParams);
  const sankeyRef = useRef(null);
  const { transformedData, loading, error, refetch, hasData } = useSankey(type, id, params);

  const handleParamsChange = (newParams) => {
    setParams(newParams);
  };

  const handleRefresh = () => {
    refetch();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getTypeDisplayName = (type) => {
    switch (type) {
      case 'proyecto':
        return 'Proyecto';
      case 'elemento':
        return 'Elemento';
      case 'tarjeta':
        return 'Tarjeta';
      default:
        return '';
    }
  };



  return (
    <div className="sankey-container">
      {showControls && (
        <SankeyControls
          onParamsChange={handleParamsChange}
          initialParams={params}
          {...controlsConfig}
        />
      )}
      
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ flex: 1, minWidth: '250px' }}>
          {transformedData?.metadata && (
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#6c757d',
              lineHeight: '1.4',
              padding: '0.75rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#495057' }}>
                📊 Información del Análisis
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                {transformedData.metadata.proyectoNombre && (
                  <div>
                    <strong>Proyecto:</strong> {transformedData.metadata.proyectoNombre}
                  </div>
                )}
                {transformedData.metadata.elementoNombre && (
                  <div>
                    <strong>Elemento:</strong> {transformedData.metadata.elementoNombre}
                  </div>
                )}
                {transformedData.metadata.tarjetaNombre && (
                  <div>
                    <strong>Tarjeta:</strong> {transformedData.metadata.tarjetaNombre}
                  </div>
                )}
                
                {(transformedData.metadata.fechaInicio || transformedData.metadata.fechaFin) && (
                  <div>
                    <strong>Período:</strong> {' '}
                    {formatDate(transformedData.metadata.fechaInicio)} - {formatDate(transformedData.metadata.fechaFin)}
                  </div>
                )}
                
                {transformedData.metadata.totalRegistros && (
                  <div>
                    <strong>Registros:</strong> {transformedData.metadata.totalRegistros.toLocaleString('es-ES')}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {transformedData.metadata.sensoresCount && (
                    <span><strong>Sensores:</strong> {transformedData.metadata.sensoresCount}</span>
                  )}
                  {transformedData.metadata.tarjetasCount && (
                    <span><strong>Tarjetas:</strong> {transformedData.metadata.tarjetasCount}</span>
                  )}
                  {transformedData.metadata.elementosCount && (
                    <span><strong>Elementos:</strong> {transformedData.metadata.elementosCount}</span>
                  )}
                </div>

                {transformedData.metadata.unidadesPrincipales && transformedData.metadata.unidadesPrincipales.length > 0 && (
                  <div>
                    <strong>Unidades:</strong> {transformedData.metadata.unidadesPrincipales.join(', ')}
                  </div>
                )}
              </div>
              
              {transformedData.metadata.fechaConsulta && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  paddingTop: '0.5rem', 
                  borderTop: '1px solid #e9ecef',
                  fontSize: '0.8rem',
                  color: '#8c8c8c'
                }}>
                  Última actualización: {formatDate(transformedData.metadata.fechaConsulta)}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Botones de exportación */}
          {hasData && !loading && !error && (
            <ExportChartButtons
              elementRef={sankeyRef}
              fileName="grafica-sankey"
              chartType="sankey"
              title={`Gráfica Sankey - ${title}`}
              analysisType={type}
              analysisName={transformedData?.metadata?.proyectoNombre || transformedData?.metadata?.elementoNombre || transformedData?.metadata?.tarjetaNombre || ''}
              showLabels={false}
              size="middle"
            />
          )}
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            style={{
              background: loading 
                ? 'linear-gradient(135deg, #8c8c8c 0%, #6c757d 100%)'
                : 'linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '500',
              boxShadow: loading 
                ? '0 2px 8px rgba(108, 117, 125, 0.3)'
                : '0 2px 8px rgba(24, 144, 255, 0.3)',
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.target.style.transform = '';
                e.target.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
              }
            }}
            onFocus={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
              }
            }}
            onBlur={(e) => {
              if (!loading) {
                e.target.style.transform = '';
                e.target.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
              }
            }}
          >
            {loading ? (
              <>
                <span style={{ 
                  width: '14px', 
                  height: '14px', 
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block'
                }}></span>
                Cargando...
              </>
            ) : (
              <>
                🔄 Actualizar
              </>
            )}
          </button>
        </div>
      </div>

      <Sankey
        ref={sankeyRef}
        data={transformedData}
        title={title}
        isNewFormat={true}
        loading={loading}
        error={error}
      />

      {!loading && !error && !hasData && (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem', 
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
          borderRadius: '12px',
          border: '2px dashed #dee2e6',
          marginTop: '1rem'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>
            📊
          </div>
          <h4 style={{ color: '#6c757d', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
            No hay datos disponibles
          </h4>
          <p style={{ color: '#6c757d', margin: '0 0 1rem 0', fontSize: '1rem', lineHeight: '1.5' }}>
            No se encontraron datos para mostrar en la gráfica Sankey con los filtros aplicados.
          </p>
          <div style={{ 
            backgroundColor: 'white',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            display: 'inline-block',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: '0.9rem', color: '#495057', marginBottom: '0.5rem', fontWeight: '600' }}>
              Sugerencias:
            </div>
            <ul style={{ fontSize: '0.9rem', color: '#6c757d', margin: 0, paddingLeft: '1.25rem' }}>
              <li>Amplía el rango de fechas seleccionado</li>
              <li>Revisa los filtros de unidades aplicados</li>
              <li>Verifica que existan sensores activos en el {getTypeDisplayName(type).toLowerCase()}</li>
              <li>Contacta al administrador si el problema persiste</li>
            </ul>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

SankeyContainer.propTypes = {
  type: PropTypes.oneOf(['proyecto', 'elemento', 'tarjeta']).isRequired,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  showControls: PropTypes.bool,
  initialParams: PropTypes.shape({
    fechaInicio: PropTypes.string,
    fechaFin: PropTypes.string,
    unidades: PropTypes.arrayOf(PropTypes.string)
  }),
  controlsConfig: PropTypes.shape({
    showUnidadesFilter: PropTypes.bool,
    showDateControl: PropTypes.bool
  })
};

export default SankeyContainer;